import { useCallback, useEffect, useMemo, useState } from "react";
import AppLayout from "../layouts/AppLayout.jsx";
import CaptionCard from "../components/CaptionCard.jsx";
import ImageCard from "../components/ImageCard.jsx";
import VideoCard from "../components/VideoCard.jsx";
import TemplateCard from "../components/TemplateCard.jsx";
import ReelCard from "../components/ReelCard.jsx";
import TemplateConfirmationModal from "../components/TemplateConfirmationModal.jsx";
import API from "../config/api.js";

export default function MyFavorites() {
  const [captions, setCaptions] = useState([]);
  const [images, setImages] = useState([]);
  const [videos, setVideos] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [reels, setReels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeType, setActiveType] = useState("all");
  const [playingReelId, setPlayingReelId] = useState(null);
  const [playingVideoId, setPlayingVideoId] = useState(null);
  const [selectedTemplate, setSelectedTemplate] = useState(null);

  const fetchList = useCallback(async (url) => {
    const formData = new FormData();
    formData.append("currentPage", 1);
    formData.append("favorites", "true");

    const res = await fetch(url, {
      method: "POST",
      credentials: "include",
      body: formData,
    });

    return res.json();
  }, []);

  const fetchFavorites = useCallback(async () => {
    try {
      setLoading(true);

      const [imagesRes, captionsRes, templatesRes, videosRes, reelsRes] =
        await Promise.all([
          fetchList(`${API.images}?list&favorites`),
          fetchList(`${API.captions}?list&favorites`),
          fetchList(`${API.templates}?list&favorites`),
          fetchList(`${API.videos}?list&favorites`),
          fetchList(`${API.reels}?list&favorites`),
        ]);

      setImages(Array.isArray(imagesRes) ? imagesRes : []);
      setCaptions(Array.isArray(captionsRes) ? captionsRes : []);
      setTemplates(Array.isArray(templatesRes) ? templatesRes : []);
      setVideos(Array.isArray(videosRes) ? videosRes : []);
      setReels(Array.isArray(reelsRes) ? reelsRes : []);
    } catch (error) {
      console.error("Error", error);
      setImages([]);
      setCaptions([]);
      setTemplates([]);
      setVideos([]);
      setReels([]);
    } finally {
      setLoading(false);
    }
  }, [fetchList]);

  useEffect(() => {
    fetchFavorites();
  }, [fetchFavorites]);

  const handleToggleReelPreview = (idreel) => {
    setPlayingReelId((prev) => (prev === idreel ? null : idreel));
  };

  const handleToggleVideoPreview = (idvideo) => {
    setPlayingVideoId((prev) => (prev === idvideo ? null : idvideo));
  };

  const openDraftPrompt = () => {
    const draftPrompt = document.getElementById("draft-prompt");

    if (draftPrompt && window.bootstrap?.Modal) {
      window.bootstrap.Modal.getOrCreateInstance(draftPrompt).show();
    }
  };

  const applyTemplateToDraft = (template) => {
    if (!template) return;

    window._draft_prompt_clear?.();

    let ids = [];

    if (Array.isArray(template.idimages)) {
      ids = template.idimages;
    } else if (
      typeof template.idimages === "string" &&
      template.idimages.trim()
    ) {
      ids = template.idimages
        .split(",")
        .map((id) => id.trim())
        .filter(Boolean);
    }

    ids.forEach((id) => {
      window._draft_prompt_item_add?.(
        "image",
        id,
        `${API.baseUrl}/upload/image/${id}/image/original.jpg`
      );
    });

    const contentInput = document.querySelector(
      '#draft-prompt [name="content"]'
    );

    if (contentInput) {
      contentInput.value = template.caption || "";
      contentInput.dispatchEvent(new Event("input", { bubbles: true }));
      contentInput.dispatchEvent(new Event("change", { bubbles: true }));
    }

    openDraftPrompt();
  };

  const handleUseTemplate = (template) => {
    if (!template) return;

    if (template.type === "images" || !template.url || template.url === "#") {
      applyTemplateToDraft(template);
      return;
    }

    setSelectedTemplate(template);
  };

  const handleCloseConfirmation = () => {
    setSelectedTemplate(null);
  };

  const handleContinueConfirmation = () => {
    applyTemplateToDraft(selectedTemplate);
    setSelectedTemplate(null);
  };

  const favoriteItems = useMemo(() => {
    const allItems = [
      ...captions.map((item) => ({
        type: "caption",
        key: `caption-${item.idcaption}`,
        data: item,
      })),
      ...images.map((item) => ({
        type: "image",
        key: `image-${item.idimage}`,
        data: item,
      })),
      ...videos.map((item) => ({
        type: "video",
        key: `video-${item.idvideo}`,
        data: item,
      })),
      ...templates.map((item) => ({
        type: "template",
        key: `template-${item.idtemplate}`,
        data: item,
      })),
      ...reels.map((item) => ({
        type: "reel",
        key: `reel-${item.idreel}`,
        data: item,
      })),
    ];

    if (activeType === "all") {
      return allItems;
    }

    return allItems.filter((item) => item.type === activeType);
  }, [captions, images, videos, templates, reels, activeType]);

  const visibleTabs = useMemo(() => {
    return {
      caption: captions.length > 0,
      image: images.length > 0,
      video: videos.length > 0,
      template: templates.length > 0,
      reel: reels.length > 0,
    };
  }, [captions, images, videos, templates, reels]);

  return (
    <AppLayout currentPage="my-favorites">
      <style>{`
        .caption.card .card-body {
          height: 300px !important;
        }
      `}</style>

      <div>
        <ul className="nav nav-tabs mb-4 justify-content-center justify-content-sm-start border-bottom favorite-menu">
          <li className="nav-item favorite-menu-all">
            <button
              type="button"
              className={`nav-link ${activeType === "all" ? "active" : ""}`}
              onClick={() => setActiveType("all")}
            >
              <i className="fi fi-rr-heart"></i>
              <span className="d-none d-md-inline-block ms-2">All</span>
            </button>
          </li>

          {visibleTabs.caption && (
            <li className="nav-item favorite-menu-caption">
              <button
                type="button"
                className={`nav-link ${activeType === "caption" ? "active" : ""}`}
                onClick={() => setActiveType("caption")}
              >
                <i className="fi fi-rr-text"></i>
                <span className="d-none d-md-inline-block ms-2">Captions</span>
              </button>
            </li>
          )}

          {visibleTabs.image && (
            <li className="nav-item favorite-menu-image">
              <button
                type="button"
                className={`nav-link ${activeType === "image" ? "active" : ""}`}
                onClick={() => setActiveType("image")}
              >
                <i className="fi fi-rr-picture"></i>
                <span className="d-none d-md-inline-block ms-2">Photos</span>
              </button>
            </li>
          )}

          {visibleTabs.video && (
            <li className="nav-item favorite-menu-video">
              <button
                type="button"
                className={`nav-link ${activeType === "video" ? "active" : ""}`}
                onClick={() => setActiveType("video")}
              >
                <i className="fi fi-rr-film"></i>
                <span className="d-none d-md-inline-block ms-2">Videos</span>
              </button>
            </li>
          )}

          {visibleTabs.template && (
            <li className="nav-item favorite-menu-template">
              <button
                type="button"
                className={`nav-link ${activeType === "template" ? "active" : ""}`}
                onClick={() => setActiveType("template")}
              >
                <i className="fi fi-rr-layout-fluid"></i>
                <span className="d-none d-md-inline-block ms-2">
                  Templates
                </span>
              </button>
            </li>
          )}

          {visibleTabs.reel && (
            <li className="nav-item favorite-menu-reel">
              <button
                type="button"
                className={`nav-link ${activeType === "reel" ? "active" : ""}`}
                onClick={() => setActiveType("reel")}
              >
                <i className="fi fi-rr-play-alt"></i>
                <span className="d-none d-md-inline-block ms-2">Reels</span>
              </button>
            </li>
          )}
        </ul>
      </div>

      {loading ? (
        <div className="text-center py-5">
          <div className="spinner-border" role="status" aria-hidden="true"></div>
        </div>
      ) : (
        <div className="row g-2" id="favorite-list">
          {favoriteItems.map((item) => {
            if (item.type === "caption") {
              return (
                <div key={item.key} className="col-md-3">
                  <CaptionCard caption={item.data} />
                </div>
              );
            }

            if (item.type === "image") {
              return (
                <div key={item.key} className="col-md-3">
                  <ImageCard image={item.data} />
                </div>
              );
            }

            if (item.type === "video") {
              return (
                <div key={item.key} className="col-md-3">
                  <VideoCard
                    video={item.data}
                    isPlaying={
                      playingVideoId ===
                      (item.data.idvideo_md5 || item.data.idvideo)
                    }
                    onTogglePreview={handleToggleVideoPreview}
                    onVideoEnded={() => setPlayingVideoId(null)}
                  />
                </div>
              );
            }

            if (item.type === "template") {
              return (
                <div key={item.key} className="col-md-3">
                  <TemplateCard
                    template={item.data}
                    onUse={handleUseTemplate}
                  />
                </div>
              );
            }

            if (item.type === "reel") {
              return (
                <div key={item.key} className="col-md-3">
                  <ReelCard
                    reel={item.data}
                    isPlaying={playingReelId === item.data.idreel}
                    onTogglePreview={handleToggleReelPreview}
                    onVideoEnded={() => setPlayingReelId(null)}
                  />
                </div>
              );
            }

            return null;
          })}

          {!favoriteItems.length && (
            <div className="col-12 text-center py-5 text-muted">
              No favorites found.
            </div>
          )}
        </div>
      )}

      <TemplateConfirmationModal
        isOpen={Boolean(selectedTemplate)}
        editUrl={selectedTemplate?.url || ""}
        onClose={handleCloseConfirmation}
        onContinue={handleContinueConfirmation}
      />
    </AppLayout>
  );
}